import { h, RefObject } from "preact";
import { useLayoutEffect } from "preact/hooks";

import { Keyboard } from "../../../../foundation/enums";
import classname from "../../../../foundation/utils/classname";
import { stopPropagation } from "../../../../foundation/utils/legacy";

import styles from "./ScreenCardNameInput.module.css";

const MAX_LENGTH = 100;

function resizeTextArea(element: HTMLTextAreaElement) {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
}

interface Props {
    class?: string;
    value: string;
    inputRef?: RefObject<HTMLTextAreaElement>;
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (event: FocusEvent) => void;
}

function ScreenCardNameInput({
    class: cssClass,
    value: initialValue,
    inputRef,
    onFocus,
    onBlur
}: Props) {
    useLayoutEffect(() => {
        const input = inputRef?.current;
        if (!input) {
            return;
        }

        input.value = initialValue;
        resizeTextArea(input);
    }, []);

    function handleInput(event: Event) {
        resizeTextArea(event.target as HTMLTextAreaElement);
    }

    function handleKeyDown(event: KeyboardEvent) {
        const input = inputRef?.current;
        if (!input) {
            return;
        }

        // See note in RichInput.
        // eslint-disable-next-line no-magic-numbers
        if (event.isComposing || event.keyCode === 229) {
            return;
        }

        switch (event.key) {
            case Keyboard.Key.Enter:
                event.preventDefault();
                input.blur();

                break;
            case Keyboard.Key.Escape:
                input.value = initialValue;
                input.blur();

                break;
            default:
                // no-op
        }
    }

    return (
        <div class={classname(styles.screenCardNameInput, cssClass)}>
            <textarea
                ref={inputRef}
                class={classname("body-1", styles.textArea)}
                maxLength={MAX_LENGTH}
                rows={1}
                onClick={stopPropagation} // Prevent card selection while renaming
                onMouseDown={stopPropagation} // Prevent drag reorder while selecting text
                onContextMenu={stopPropagation} // Prevent card context menu while renaming
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}/>
        </div>
    );
}

export default ScreenCardNameInput;
